"use client";

import { useEffect, useState, useTransition } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Plus } from "lucide-react";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { fetchPricingResolveItems, pricingItemsById } from "@/lib/pricing/pricing-resolve-client";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { cn } from "@/lib/utils";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { ESSENTIAL_ACCESSORIES } from "@/config/brand";
import { addCartLine, fetchProductForUpsell } from "./actions";
import { useGateCartAction } from "@/hooks/use-gate-cart-action";

interface CartEssentialsProps {
	lines: Array<{
		productSlug: string;
		categoryName: string | null;
	}>;
	checkoutId: string;
	channel: string;
	locale: string;
}

export function CartEssentials({ lines, checkoutId, channel, locale }: CartEssentialsProps) {
	const t = useTranslations("cart");
	const gateAction = useGateCartAction();
	const ploc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const [isPending, startTransition] = useTransition();
	const [added, setAdded] = useState(false);
	const [accessory, setAccessory] = useState<{
		name: string;
		thumbnailUrl: string;
		variantId: string;
		price: number;
		currency: string;
	} | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [pricePresentation, setPricePresentation] = useState<ResolvedMoneyPresentation | null>(null);

	useEffect(() => {
		let cancelled = false;

		async function resolve() {
			setIsLoading(true);

			const cartCategories = new Set(lines.map((l) => l.categoryName).filter(Boolean));
			const cartSlugs = new Set(lines.map((l) => l.productSlug));

			for (const acc of ESSENTIAL_ACCESSORIES) {
				if (cartSlugs.has(acc.slug)) continue;
				const hasMatchingCategory = acc.categoryTriggers.some((cat) => cartCategories.has(cat));
				if (!hasMatchingCategory) continue;

				try {
					const productData = await fetchProductForUpsell(acc.slug, channel, locale);
					if (!productData || cancelled) continue;

					const available = productData.variants
						.filter((v) => v.quantityAvailable == null || v.quantityAvailable > 0)
						.sort((a, b) => a.price - b.price);

					if (available.length === 0) continue;

					const cheapest = available[0]!;
					setAccessory({
						name: productData.name,
						thumbnailUrl: normalizeSaleorMediaUrl(productData.thumbnailUrl),
						variantId: cheapest.id,
						price: cheapest.price,
						currency: cheapest.currency,
					});
					setIsLoading(false);
					return;
				} catch {
					continue;
				}
			}

			if (!cancelled) {
				setAccessory(null);
				setIsLoading(false);
			}
		}

		resolve();
		return () => {
			cancelled = true;
		};
	}, [lines, channel, locale]);

	useEffect(() => {
		if (!accessory) {
			queueMicrotask(() => setPricePresentation(null));
			return;
		}

		let cancelled = false;
		(async () => {
			const rows = await fetchPricingResolveItems({
				locale,
				channel,
				items: [
					{
						id: "price",
						amount: accessory.price,
						currency: accessory.currency,
					},
				],
			});
			if (cancelled || !rows) return;
			setPricePresentation(pricingItemsById(rows).get("price") ?? null);
		})();

		return () => {
			cancelled = true;
		};
	}, [accessory, locale, channel]);

	const handleAdd = () => {
		if (!accessory) return;
		gateAction(() => {
			startTransition(async () => {
				await addCartLine(checkoutId, accessory.variantId);
				setAdded(true);
			});
		});
	};

	// Hide when loading, no accessory found, or already added
	if (isLoading || !accessory || added) return null;

	return (
		<div className="px-5 py-2.5">
			<div className="bg-secondary/20 flex items-center gap-3 rounded-lg border border-dashed border-border p-2.5">
				{accessory.thumbnailUrl && (
					<div className="relative h-9 w-8 shrink-0 overflow-hidden rounded bg-secondary">
						<Image
							src={accessory.thumbnailUrl}
							alt={accessory.name}
							fill
							className="object-cover"
							sizes="32px"
						/>
					</div>
				)}
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium">{accessory.name}</p>
					<p className="text-xs text-muted-foreground">{t("frequentlyOrderedTogether")}</p>
				</div>
				<span className="shrink-0 text-sm font-medium">
					{pricePresentation?.primaryLabel ?? formatPrice(accessory.price, accessory.currency, ploc)}
				</span>
				<button
					type="button"
					disabled={isPending}
					onClick={handleAdd}
					className={cn(
						"inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-foreground text-background transition-opacity hover:opacity-90",
						isPending && "cursor-not-allowed opacity-50",
					)}
					aria-label={`${t("addToCart")} ${accessory.name}`}
				>
					<Plus className="h-3.5 w-3.5" />
				</button>
			</div>
		</div>
	);
}
